import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { onProgress, onPhaseChange, onComplete as onLoadingComplete } from '../../utils/loadingService.js'

/**
 * LoadingScreen - Real-time loading screen tracking actual app initialization
 * Shows cache loading progress with animated progress bars
 */
export function LoadingScreen({ onComplete }) {
  const [loadingPhase, setLoadingPhase] = useState('CACHE_HYDRATION')
  const [progress, setProgress] = useState(0)
  const [detailText, setDetailText] = useState('Initializing...')
  
  // Loading phases - mapped from loadingService.js
  const phases = {
    CACHE_HYDRATION: { label: 'Loading Cache', duration: 0 },
    PRODUCT_LIST: { label: 'Loading Cell List', duration: 0 },
    MINIMAL_DATA: { label: 'Loading Cell Data', duration: 0 },
    GLOBAL_STATS: { label: 'Computing Statistics', duration: 0 },
    ready: { label: 'Ready', duration: 0 }
  }
  
  useEffect(() => {
    // Subscribe to real loading events from loadingService
    const unsubProgress = onProgress((data) => {
      setProgress(Math.round(data.totalProgress))
      setDetailText(data.label || 'Loading...')
      // Progress update
    })
    
    const unsubPhaseChange = onPhaseChange((data) => {
      setLoadingPhase(data.phase)
      setDetailText(data.label || 'Loading...')
      // Phase change
    })
    
    const unsubComplete = onLoadingComplete((data) => {
      // Loading complete
      setLoadingPhase('ready')
      setProgress(100)
      setDetailText('Ready!')
      
      // Wait 600ms before calling onComplete callback
      setTimeout(() => {
        if (onComplete) onComplete()
      }, 600)
    })
    
    // Cleanup subscriptions
    return () => {
      unsubProgress()
      unsubPhaseChange()
      unsubComplete()
    }
  }, [onComplete])
  
  return (
    <div className="loading-screen">
      {/* Same background as login page */}
      <div className="login-background">
        {/* Animated gradient background */}
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        
        {/* Matrix-style raining logos - 40 logos for dense effect (same as login) */}
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-1" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-2" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-3" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-4" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-5" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-6" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-7" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-8" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-9" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-10" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-11" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-12" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-13" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-14" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-15" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-16" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-17" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-18" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-19" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-20" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-21" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-22" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-23" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-24" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-25" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-26" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-27" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-28" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-29" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-30" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-31" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-32" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-33" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-34" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-35" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-36" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-37" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-38" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-39" />
        <img src="/logo/bildmarke.svg" alt="" className="radar-logo radar-logo-40" />
      </div>
      
      {/* Loading Content */}
      <div className="loading-container">
        {/* Logo - Same as login page */}
        <div className="login-logo loading-logo">
          <div className="login-logo-container">
            <img src="/logo/bildmarke.svg" alt="Batemo" className="logo-icon" />
            <div className="logo-text-stack">
              <h1>BATEMO INSIGHTS</h1>
              <p className="login-subtitle">Understanding Batteries</p>
            </div>
          </div>
        </div>
        
        {/* Main Loading Message */}
        <div className="loading-message">
          <h1>Loading Your Experience</h1>
          <p className="loading-detail">{detailText}</p>
        </div>
        
        {/* Progress Bar */}
        <div className="loading-progress-container">
          <div className="loading-progress-track">
            <div
              className="loading-progress-fill"
              style={{ width: `${progress}%` }}
            >
              <div className="loading-progress-glow"></div>
            </div>
          </div>
          
          {/* Progress Percentage */}
          <div className="loading-percentage">{progress}%</div>
        </div>
        
        {/* Phase Indicators */}
        <div className="loading-phases">
          {Object.entries(phases).map(([key, phase]) => (
            <div
              key={key}
              className={`loading-phase ${loadingPhase === key ? 'active' : ''} ${
                Object.keys(phases).indexOf(loadingPhase) > Object.keys(phases).indexOf(key) ? 'complete' : ''
              }`}
            >
              <div className="phase-dot"></div>
              <span className="phase-label">{phase.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
